import type { ProjectDefinition } from "../../../types";
import { type CellValue, type FuncionProjectSpec, conditions, seededRandom } from "../spec";

const SHEET = "AV Reporte";
const MESES = [
  { tabla: "VentasEne", anchor: "A4", n: 6 },
  { tabla: "VentasFeb", anchor: "E4", n: 8 },
  { tabla: "VentasMar", anchor: "I4", n: 7 },
] as const;
// Plain first letters keep Excel's and JavaScript's alphabetical order identical.
const VENDEDORES = ["Ana López", "Luis Martínez", "Marta Sánchez", "Jorge Ramírez", "Sofía Torres", "Diego Castillo", "Paola Jiménez", "Ricardo Vargas"];
const REGIONES = ["Norte", "Sur", "Centro"] as const;

const rnd = seededRandom(5858);
// Each month lists a different subset of sellers; amounts keep every total distinct.
const shuffled = () => {
  const list = [...VENDEDORES];
  for (let i = list.length - 1; i > 0; i--) {
    const j = rnd.int(0, i);
    [list[i], list[j]] = [list[j], list[i]];
  }
  return list;
};
const DATA = MESES.map((mes, m) =>
  shuffled()
    .slice(0, mes.n)
    .map((v, i) => [v, rnd.pick(REGIONES), 100000 + rnd.int(0, 400) * 1000 + m * 31 + i * 7] as [string, string, number])
);

const todos = DATA.flat();
const byText = (a: string, b: string) => a.localeCompare(b, "es", { sensitivity: "base" });
const vendedores = [...new Set(todos.map((r) => r[0]))].sort(byText);
const totales = vendedores.map((v) => [v, todos.filter((r) => r[0] === v).reduce((s, r) => s + r[2], 0)] as [string, number]);
const top3 = [...totales].sort((a, b) => b[1] - a[1]).slice(0, 3);

const col = (values: CellValue[]) => values.map((v) => [v]);
const START = 16;
const A = {
  consolidado: `N${START}`,
  lista: `R${START}`,
  totales: `T${START}`,
  top: `T${START + vendedores.length + 4}`,
  topEncabezado: `T${START + vendedores.length + 10}`,
};

const TOTALES_FORMULA =
  "=LET(v,ORDENAR(UNICOS(APILARV(VentasEne[Vendedor],VentasFeb[Vendedor],VentasMar[Vendedor]))),APILARH(v,SUMAR.SI(VentasEne[Vendedor],v,VentasEne[Ventas])+SUMAR.SI(VentasFeb[Vendedor],v,VentasFeb[Ventas])+SUMAR.SI(VentasMar[Vendedor],v,VentasMar[Ventas])))";

const EXPECTED: [string, string[], CellValue[][], Record<string, number>?][] = [
  [A.consolidado, ["VSTACK"], todos],
  [A.lista, ["SORT", "UNIQUE", "VSTACK"], col(vendedores)],
  [A.totales, ["LET", "HSTACK", "SUMIF"], totales, { SUMIF: 3 }],
  [A.top, ["TAKE", "SORT"], top3],
  [A.topEncabezado, ["VSTACK", "HSTACK", "TAKE"], [["Vendedor", "Total"], ...top3]],
];

export const spec: FuncionProjectSpec = {
  projectId: "funciones-reporte-trimestral",
  sheets: [
    {
      name: SHEET,
      title: "Reporte trimestral automático",
      subtitle: "Una tabla por mes con vendedores distintos — arma el reporte del trimestre con fórmulas que crecen solas",
      tables: MESES.map((mes, m) => ({
        name: mes.tabla,
        anchor: mes.anchor,
        headers: ["Vendedor", "Región", "Ventas"],
        rows: DATA[m],
        formats: { Ventas: "$#,##0" },
      })),
      answers: [
        { address: A.consolidado, label: "Tarea 1 · Consolidado", labelPosition: "above" },
        { address: A.lista, label: "Tarea 2 · Vendedores", labelPosition: "above" },
        { address: A.totales, label: "Tarea 3 · Total por vendedor", labelPosition: "above" },
        { address: A.top, label: "Tarea 4 · Top 3", labelPosition: "above" },
        { address: A.topEncabezado, label: "Tarea 5 · Top 3 con encabezado", labelPosition: "above" },
      ],
    },
  ],
  checks: Object.fromEntries(
    EXPECTED.map(([address, fns, expected, minCounts], i) => [
      i + 1,
      { kind: "spill" as const, sheet: SHEET, address, fns, expected, minCounts },
    ])
  ),
};

const F = (text: string) => `<span class="formula">${text}</span>`;

export const project: ProjectDefinition = {
  id: "funciones-reporte-trimestral",
  title: "Reto final: Reporte trimestral automático",
  subtitle: "APILARV, UNICOS, LET, TOMAR y # en un solo reporte",
  description:
    "Cada mes llega una tabla de ventas, y no siempre aparecen los mismos vendedores: en enero vendieron 6, en febrero 8 y en marzo 7. Dirección quiere el consolidado, la lista de vendedores, el total del trimestre por vendedor y el top 3 con encabezado, todo automático.\n\nEs el tipo de reporte que antes requería macros o tablas dinámicas y hoy se arma con una cadena de fórmulas.",
  topics: [
    "Consolidar tablas de distinto tamaño",
    "Lista maestra de un consolidado",
    "LET para no repetir cálculos",
    "Totales con SUMAR.SI sobre una lista derramada",
    "Top N con encabezado: APILARV(APILARH(...), ...)",
  ],
  tasks: [
    {
      id: 1,
      level: "intermedio",
      title: "Tarea 1: Consolidado del trimestre",
      description: `En la hoja <strong>AV Reporte</strong>, celda <strong>${A.consolidado}</strong>: junta <strong>VentasEne</strong>, <strong>VentasFeb</strong> y <strong>VentasMar</strong> una debajo de otra. Tienen distinto número de filas, y no importa.`,
      solution: [`Selecciona ${F(A.consolidado)}.`, `Escribe ${F("=APILARV(VentasEne,VentasFeb,VentasMar)")}.`],
      expectedResult: "21 filas × 3 columnas",
      finalFormula: "=APILARV(VentasEne,VentasFeb,VentasMar)",
    },
    {
      id: 2,
      level: "intermedio",
      title: "Tarea 2: Lista de vendedores",
      description: `En <strong>${A.lista}</strong>: todos los vendedores que aparecen en algún mes, <strong>sin repetir</strong> y en orden alfabético.`,
      solution: [
        `Selecciona ${F(A.lista)}.`,
        "Apila solo las columnas de vendedor de los tres meses y luego limpia y ordena.",
        `Escribe ${F("=ORDENAR(UNICOS(APILARV(VentasEne[Vendedor],VentasFeb[Vendedor],VentasMar[Vendedor])))")}.`,
      ],
      expectedResult: "Lista alfabética de vendedores",
      finalFormula: "=ORDENAR(UNICOS(APILARV(VentasEne[Vendedor],VentasFeb[Vendedor],VentasMar[Vendedor])))",
    },
    {
      id: 3,
      level: "reto",
      title: "Tarea 3: Total del trimestre por vendedor",
      description: conditions(
        `En <strong>${A.totales}</strong>: una tabla de 2 columnas con cada vendedor y su <strong>total de los tres meses</strong>:`,
        [
          "Con <strong>LET</strong>, guarda la lista de la Tarea 2 como <strong>v</strong>",
          "Suma tres <strong>SUMAR.SI</strong> (uno por mes) usando <strong>v</strong> como criterio: calculan un total por cada vendedor a la vez",
          "Pon lado a lado <strong>v</strong> y los totales con <strong>APILARH</strong>",
        ]
      ),
      solution: [
        `Selecciona ${F(A.totales)}.`,
        "Si un vendedor no aparece en un mes, SUMAR.SI devuelve 0 para ese mes, así que no hace falta ningún SI.ERROR.",
        `Escribe ${F(TOTALES_FORMULA)}.`,
      ],
      expectedResult: "Vendedor | Total del trimestre",
      finalFormula: TOTALES_FORMULA,
    },
    {
      id: 4,
      level: "reto",
      title: "Tarea 4: Top 3 del trimestre",
      description: `En <strong>${A.top}</strong>: los <strong>3 vendedores con mayor total</strong>, usando el resultado de la Tarea 3 con el operador <strong>#</strong>.`,
      solution: [
        `Selecciona ${F(A.top)}.`,
        `${F(`${A.totales}#`)} es la tabla de totales completa. Ordénala por la columna 2 de mayor a menor y toma 3 filas.`,
        `Escribe ${F(`=TOMAR(ORDENAR(${A.totales}#,2,-1),3)`)}.`,
      ],
      expectedResult: "3 filas × 2 columnas",
      finalFormula: `=TOMAR(ORDENAR(${A.totales}#,2,-1),3)`,
    },
    {
      id: 5,
      level: "reto",
      title: "Tarea 5: Top 3 con encabezado",
      description: conditions(`En <strong>${A.topEncabezado}</strong>: el mismo top 3, pero con una fila de encabezado <strong>Vendedor | Total</strong> arriba:`, [
        "<strong>APILARH(\"Vendedor\",\"Total\")</strong> crea la fila de encabezado (sin usar constantes con llaves)",
        "<strong>APILARV</strong> la pone encima del top 3",
      ]),
      solution: [
        `Selecciona ${F(A.topEncabezado)}.`,
        `Escribe ${F(`=APILARV(APILARH("Vendedor","Total"),TOMAR(ORDENAR(${A.totales}#,2,-1),3))`)}.`,
      ],
      expectedResult: "4 filas: encabezado + top 3",
      finalFormula: `=APILARV(APILARH("Vendedor","Total"),TOMAR(ORDENAR(${A.totales}#,2,-1),3))`,
    },
  ],
};
